import React, { useState } from "react";
import { Mail, Music, User, MessageSquare, Send, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Booking = () => {
  const [formData, setFormData] = useState({
    artistName: "",
    contactName: "",
    email: "",
    phone: "",
    genre: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create mailto link with form data
    const subject = encodeURIComponent(`Booking Inquiry: ${formData.artistName}`);
    const body = encodeURIComponent(
      `Artist/Band Name: ${formData.artistName}\n` +
      `Contact Name: ${formData.contactName}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Genre: ${formData.genre}\n\n` +
      `Message:\n${formData.message}`
    );

    // Open email client
    window.location.href = `mailto:Jeff@midamericatalent.com?subject=${subject}&body=${body}`;
    
    toast.success("Opening your email client...");
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-obsidian">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">
            Artist <span className="text-electric">Booking</span>
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Interested in performing at The BrownStone Jazz Club? Submit your booking inquiry below.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Booking Agent Info */}
          <div className="lg:col-span-2">
            <div className="bg-obsidian-light border border-white/10 rounded-xl p-6 sticky top-24">
              {/* MidAmerica Talent Logo */}
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_960d1e88-6284-4c27-88db-66afded493ab/artifacts/e9x5qqnk_IMG_20260213_225606.png" 
                  alt="MidAmerica Talent" 
                  className="w-32 h-32 mx-auto rounded-full border-2 border-electric/30"
                />
              </div>

              <h2 className="font-display text-xl text-white mb-2 text-center">Booking Agent</h2>
              <p className="text-electric text-center font-medium mb-4">MidAmerica Talent</p>
              
              <div className="space-y-4 text-white/70">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-electric flex-shrink-0" />
                  <a 
                    href="mailto:Jeff@midamericatalent.com" 
                    className="hover:text-electric transition-colors break-all"
                  >
                    Jeff@midamericatalent.com
                  </a>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-sm leading-relaxed">
                  All booking inquiries are handled through MidAmerica Talent. 
                  Fill out the form or email directly to discuss performance opportunities.
                </p>
              </div>

              <a 
                href="mailto:Jeff@midamericatalent.com"
                className="mt-4 flex items-center justify-center gap-2 w-full py-3 bg-electric/10 hover:bg-electric/20 text-electric rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email Directly
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-obsidian-light border border-white/10 rounded-xl p-6 sm:p-8">
              <h3 className="font-display text-xl text-white mb-6">Booking Inquiry Form</h3>

              <div className="space-y-5">
                {/* Artist/Band Name */}
                <div>
                  <label htmlFor="artistName" className="block text-white/70 text-sm mb-2">
                    Artist / Band Name *
                  </label>
                  <div className="relative">
                    <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type="text"
                      id="artistName"
                      name="artistName"
                      value={formData.artistName}
                      onChange={handleChange}
                      required
                      className="w-full bg-obsidian border border-white/10 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/30 focus:border-electric focus:outline-none transition-colors"
                      placeholder="Your artist or band name"
                      data-testid="booking-artist-name"
                    />
                  </div>
                </div>

                {/* Contact Name */}
                <div>
                  <label htmlFor="contactName" className="block text-white/70 text-sm mb-2">
                    Contact Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      className="w-full bg-obsidian border border-white/10 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/30 focus:border-electric focus:outline-none transition-colors"
                      placeholder="Your name"
                      data-testid="booking-contact-name"
                    />
                  </div>
                </div>

                {/* Email & Phone Row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-white/70 text-sm mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-obsidian border border-white/10 rounded-lg py-3 px-4 text-white placeholder-white/30 focus:border-electric focus:outline-none transition-colors"
                      placeholder="your@email.com"
                      data-testid="booking-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-white/70 text-sm mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-obsidian border border-white/10 rounded-lg py-3 px-4 text-white placeholder-white/30 focus:border-electric focus:outline-none transition-colors"
                      placeholder="(555) 555-5555"
                      data-testid="booking-phone"
                    />
                  </div>
                </div>

                {/* Genre */}
                <div>
                  <label htmlFor="genre" className="block text-white/70 text-sm mb-2">
                    Genre / Style *
                  </label>
                  <select
                    id="genre"
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    required
                    className="w-full bg-obsidian border border-white/10 rounded-lg py-3 px-4 text-white focus:border-electric focus:outline-none transition-colors appearance-none"
                    data-testid="booking-genre"
                  >
                    <option value="" disabled>Select your genre</option>
                    <option value="Jazz">Jazz</option>
                    <option value="Blues">Blues</option>
                    <option value="Soul">Soul</option>
                    <option value="R&B">R&B</option>
                    <option value="Funk">Funk</option>
                    <option value="Latin Jazz">Latin Jazz</option>
                    <option value="Fusion">Fusion</option>
                    <option value="Vocal Jazz">Vocal Jazz</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-white/70 text-sm mb-2">
                    Tell us about yourself *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-white/30" />
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-obsidian border border-white/10 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/30 focus:border-electric focus:outline-none transition-colors resize-none"
                      placeholder="Share your experience, notable performances, and why you'd like to play at The BrownStone..."
                      data-testid="booking-message"
                    />
                  </div>
                </div>

                {/* Links */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    Include links to your music (Spotify, YouTube, website, etc.)
                  </label>
                  <p className="text-white/40 text-xs mb-2">Add these in the message above</p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-electric text-obsidian hover:bg-electric-light font-medium py-6 gap-2"
                  data-testid="booking-submit"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Booking Inquiry
                    </>
                  )}
                </Button>

                <p className="text-white/40 text-xs text-center">
                  Your inquiry will be sent to Jeff at MidAmerica Talent
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
